import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const markerClass = {
  appointment: "bg-emerald-500 text-white font-700 shadow-sm",
  reception: "bg-sky-500 text-white font-700 shadow-sm",
  weekend: "bg-rose-400 text-white font-700",
  holiday: "bg-rose-600 text-white font-700",
  vacation: "bg-amber-400 text-white font-700",
  unlocked: "bg-emerald-500 text-white font-700 shadow-sm",
  booked: "bg-blue-600 text-white font-700",
  available: "bg-emerald-500 text-white",
  blocked: "bg-slate-800 text-white font-700 line-through",
  selected: "bg-blue-50 text-blue-800 font-700 ring-2 ring-inset ring-blue-600",
};

function dayStateClass(disabled, marker) {
  if (disabled) {
    if (marker) return `${markerClass[marker]} opacity-50 cursor-not-allowed`;
    return "cursor-not-allowed text-slate-300";
  }
  if (marker) return `${markerClass[marker]} hover:brightness-95`;
  return "text-slate-700 hover:bg-slate-100";
}

export function MonthCalendar({
  value,
  onSelect,
  isDisabled = () => false,
  getMarker = () => null, // returns "holiday" | "booked" | "vacation" | "available" | null
  minMonth,
  maxMonth,
}) {
  const start = value ? new Date(value + "T00:00:00") : new Date();
  const [cursor, setCursor] = useState(new Date(start.getFullYear(), start.getMonth(), 1));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const canPrev = !minMonth || cursor > new Date(minMonth.getFullYear(), minMonth.getMonth(), 1);
  const canNext = !maxMonth || cursor < new Date(maxMonth.getFullYear(), maxMonth.getMonth(), 1);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="month-calendar">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          disabled={!canPrev}
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-30"
          data-testid="cal-prev"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="font-heading text-sm font-700 text-slate-900">
          {MONTHS[month]} {year}
        </span>
        <button
          type="button"
          disabled={!canNext}
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-30"
          data-testid="cal-next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="mb-2 grid grid-cols-7 gap-1.5">
        {WD.map((w, idx) => (
          <div key={w} className={`text-center text-[11px] font-700 ${idx >= 5 ? "text-rose-400" : "text-slate-400"}`}>{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} />;
          const dIso = iso(d);
          const disabled = isDisabled(dIso);
          const marker = getMarker(dIso);
          const selected = value === dIso;
          const stateCls = dayStateClass(disabled, marker);
          const selCls = selected ? "ring-2 ring-slate-900 ring-offset-1" : "";
          return (
            <button
              key={dIso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect && onSelect(dIso)}
              data-testid={`cal-day-${dIso}`}
              className={`relative flex h-11 items-center justify-center rounded-lg text-sm transition-all ${stateCls} ${selCls}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
